import { iChatData } from "../../types";

export interface MaskedText {
  userInput: string;
  voiceReply?: string;
}

export interface MaskedSwap {
  role: "user" | "assistant";
  raw: string;
  masked: string;
}

export const readMaskedFrame = (frame: any, voiceExchange: boolean): MaskedText | null => {
  if (typeof frame?.userInput !== "string") return null;
  if (!voiceExchange) return { userInput: frame.userInput };
  return typeof frame.voiceReply === "string" ? { userInput: frame.userInput, voiceReply: frame.voiceReply } : null;
};

export const maskedSwaps = (sent: any, masked: MaskedText): MaskedSwap[] =>
  [
    { role: "user" as const, raw: sent?.userInput, masked: masked.userInput },
    { role: "assistant" as const, raw: sent?.voiceReply, masked: masked.voiceReply },
  ].filter(
    (swap): swap is MaskedSwap =>
      typeof swap.raw === "string" && swap.raw !== "" && typeof swap.masked === "string" && swap.raw !== swap.masked
  );

const swapFor = (swaps: MaskedSwap[], role: unknown, text: unknown): MaskedSwap | undefined =>
  swaps.find((swap) => swap.role === role && swap.raw === text);

export const withMaskedMessages = (messages: any[], swaps: MaskedSwap[]): any[] =>
  messages.some((message) => swapFor(swaps, message?.role, message?.content))
    ? messages.map((message) => {
        const swap = swapFor(swaps, message?.role, message?.content);
        return swap ? { ...message, content: swap.masked } : message;
      })
    : messages;

export const withMaskedPayload = (payload: any, swaps: MaskedSwap[]): any => {
  const swap = swapFor(swaps, "user", payload?.userInput);
  if (!swap) return payload;
  return {
    ...payload,
    userInput: swap.masked,
    ...(Array.isArray(payload.messages) ? { messages: withMaskedMessages(payload.messages, swaps) } : {}),
  };
};

export const requestEntryId = (
  chatData: iChatData[],
  outbound: { messages: any[]; pendingEntryId: string } | null,
  sent: any
): string | null => {
  const last = outbound?.messages?.[outbound.messages.length - 1];
  if (!outbound || last?.role !== "user" || last?.content !== sent?.userInput) return null;
  const at = chatData.findIndex((entry) => entry.id === outbound.pendingEntryId);
  const entry = at > 0 ? chatData[at - 1] : undefined;
  return entry?.isUserMessage ? entry.id : null;
};

export interface MaskedTargets {
  requestId: string | null;
  replyTraceId: string | null;
}

const maskedLine = (entry: iChatData, swaps: MaskedSwap[], targets: MaskedTargets): MaskedSwap | undefined => {
  if (entry.isUserMessage) {
    return entry.id === targets.requestId ? swaps.find((swap) => swap.role === "user") : swapFor(swaps, "user", entry.message);
  }
  const reply = swaps.find((swap) => swap.role === "assistant");
  if (!reply) return undefined;
  return (targets.replyTraceId !== null && entry.traceId === targets.replyTraceId) || entry.message === reply.raw ? reply : undefined;
};

const maskEntry = (entry: iChatData, swaps: MaskedSwap[], targets: MaskedTargets): iChatData => {
  const line = maskedLine(entry, swaps, targets);
  const prompt = Array.isArray(entry.prompt) ? withMaskedMessages(entry.prompt, swaps) : entry.prompt;
  if ((!line || entry.maskedMessage === line.masked) && prompt === entry.prompt) return entry;
  return { ...entry, ...(line ? { maskedMessage: line.masked } : {}), prompt };
};

export const withMaskedChat = (chatData: iChatData[], swaps: MaskedSwap[], targets: MaskedTargets): iChatData[] => {
  const next = chatData.map((entry) => maskEntry(entry, swaps, targets));
  return next.some((entry, index) => entry !== chatData[index]) ? next : chatData;
};

export const maskedEntryIds = (chatData: iChatData[], swaps: MaskedSwap[], targets: MaskedTargets): string[] =>
  chatData.filter((entry) => maskEntry(entry, swaps, targets) !== entry).map((entry) => entry.id);
